import { ReactNode, useState } from 'react';

import { Box, Flex, List, ListItem, Text, useBreakpointValue } from '@chakra-ui/react';

import { trackEvent } from '~/features/analytics/track-event';
import { CatalogItem } from '~/features/catalog/models/catalog-item';
import { billingFrequencySchema } from '~/features/checkout/models/checkout';
import { usePricingExperimentVariant } from '~/features/experiments/topaz/hooks';
import { shouldSeeAnnualPlan } from '~/features/experiments/topaz/utils';
import { useCurrency } from '~/features/internationalization/hooks/use-currency';
import { PromotionsBanner } from '~/features/plans/components/individual-plans-section-v2/promotions-banner';
import { TeamPricePlan } from '~/features/plans/components/team-price-plan';
import { useStandaloneProgramPrice } from '~/features/plans/hooks/use-standalone-program-price';
import { planIdSchema, PlanMetadata } from '~/features/plans/models/plan-metadata';
import { STANDALONE_FEATURES } from '~/features/program/components/price-card';
import { RelatedProgramsSection } from '~/features/program/components/related-programs-section';

import { IndividualSignUpButton, TeamSignUpButton } from '../sign-up-button';

import { BundlePriceListItem } from './bundle-price-list-item';
import { IndividualPlanCard } from './individual-plan-card';
import { IndividualPlansSectionV2Layout } from './layout';
import { StandalonePurchaseCard } from './one-time-purchase-card';

export type IndividualPlansSectionV2Props = {
  title?: string;
  programTitle?: string;
  programKey?: string;
  relatedPrograms: CatalogItem[];
  buttonTheme?: 'blue' | 'lime';
  showTeamCard?: boolean;
  footerSlot?: ReactNode;
  teamPlanMetadata?: PlanMetadata;
};

export function IndividualPlansSectionV2(props: IndividualPlansSectionV2Props) {
  const [priceHeights, setPriceHeights] = useState<number[]>([126, 126, 126]);
  const { currency } = useCurrency();
  const { data: price, isLoading } = useStandaloneProgramPrice({ currency, nodeKey: props.programKey });
  const { variant: pricingVariant } = usePricingExperimentVariant();
  const isBundleAnnualPlan = shouldSeeAnnualPlan(currency, pricingVariant);
  const hasStandalonePurchaseOption = !isLoading && Boolean(price);

  const isEnrollNowButtonsAligned = useBreakpointValue({
    base: false,
    xl: true,
  });

  const bundlePlanKey = isBundleAnnualPlan
    ? billingFrequencySchema.enum['annually']
    : billingFrequencySchema.enum['4month'];

  const fourMonthMetadata = {
    id: planIdSchema.Enum.individual,
    title: '4-Month Subscription',
    features: [
      {
        label: 'All the **same great benefits as our monthly plan**',
      },
      {
        label: 'The most cost-effective way to develop the skills you want',
      },
    ],
  };

  const annuallyMetadata = {
    id: planIdSchema.Enum.individual,
    title: 'Annual Subscription',
    features: [
      {
        label: 'All the **same great benefits as our monthly plan**',
      },
      {
        label: 'The most cost-effective way to develop the skills you want',
      },
    ],
  };

  const monthToMonthMetadata = {
    id: planIdSchema.Enum.individual,
    title: 'Monthly Subscription',
    features: [
      {
        label: '**Cancel Anytime**',
      },
      {
        label: '**Unlimited access to hundreds of top-rated courses**',
      },
      {
        label: 'Hands-on projects with expert feedback',
      },
      {
        label: 'Personalized career coaching and interview prep',
      },
      {
        label: 'Program Certificates',
      },
    ],
  };

  return (
    <IndividualPlansSectionV2Layout
      title={props.title}
      titleSize={props.showTeamCard ? 'h1' : 'h2'} // showTeamCard => Plan Page title
      cardsSlot={
        <Flex alignItems='center' flexDir='column' gap={8}>
          <Flex
            justifyContent='space-evenly'
            margin='0 auto'
            maxWidth={hasStandalonePurchaseOption || props.showTeamCard ? 'unset' : '924px'}
            flexDir={{
              base: 'column',
              xl: 'row',
            }}
            gap={
              hasStandalonePurchaseOption
                ? 4
                : {
                    base: 12,
                    xl: 10,
                  }
            }
          >
            <IndividualPlanCard
              key={billingFrequencySchema.enum.monthly}
              billingFrequency={billingFrequencySchema.enum.monthly}
              metadata={monthToMonthMetadata}
              syncRenderedPriceHeight={isEnrollNowButtonsAligned}
              setPriceHeight={(newHeight) =>
                setPriceHeights((prev) => prev.map((prevHeight, idx) => (idx === 0 ? newHeight : prevHeight)))
              }
              signUpButtonSlot={
                <IndividualSignUpButton
                  ariaLabel='Sign up for the Individual "Month-To-Month" Plan'
                  billingFrequency={billingFrequencySchema.enum.monthly}
                  colorScheme={props.buttonTheme}
                  programKey={props.programKey}
                  onClick={() =>
                    trackEvent({
                      name: 'Product Page CTA Clicked',
                      type: 'click',
                      label: 'monthly_enroll_now',
                      additionalAttributes: {
                        product_ids: [props.programKey].filter(Boolean),
                      },
                    })
                  }
                />
              }
            />
            <IndividualPlanCard
              key={bundlePlanKey}
              billingFrequency={bundlePlanKey}
              metadata={isBundleAnnualPlan ? annuallyMetadata : fourMonthMetadata}
              syncRenderedPriceHeight={isEnrollNowButtonsAligned}
              containerProps={{
                marginTop: hasStandalonePurchaseOption
                  ? {
                      base: '32px',
                      xl: '0px',
                    }
                  : undefined,
              }}
              footnoteSlot={
                <List as='ol' color='gray.700'>
                  <ListItem>
                    <Text as='small' fontSize='xs'>
                      <Box as='sup'>1</Box>Discount applies to the{' '}
                      {isBundleAnnualPlan ? 'first year' : 'first 4 months'} of membership. After that, plans are
                      converted to month-to-month.
                    </Text>
                  </ListItem>
                  <BundlePriceListItem billingFrequency={bundlePlanKey} />
                </List>
              }
              setPriceHeight={(newHeight) =>
                setPriceHeights((prev) => prev.map((prevHeight, idx) => (idx === 1 ? newHeight : prevHeight)))
              }
              signUpButtonSlot={
                <IndividualSignUpButton
                  colorScheme={props.buttonTheme}
                  programKey={props.programKey}
                  ariaLabel={
                    isBundleAnnualPlan
                      ? 'Sign up for the Individual "Annual" Plan'
                      : 'Sign up for the Individual "Four Month" Plan'
                  }
                  billingFrequency={
                    isBundleAnnualPlan ? billingFrequencySchema.enum['annually'] : billingFrequencySchema.enum['4month']
                  }
                  onClick={() =>
                    trackEvent({
                      name: 'Product Page CTA Clicked',
                      type: 'click',
                      label: isBundleAnnualPlan ? 'annual_bundle_enroll_now' : '4month_bundle_enroll_now',
                      additionalAttributes: {
                        product_ids: [props.programKey].filter(Boolean),
                      },
                    })
                  }
                />
              }
              isBestValueFrameRendered
            />
            {hasStandalonePurchaseOption && !!props.programKey && !!props.programTitle && (
              <StandalonePurchaseCard
                key='standalone'
                nodeKey={props.programKey}
                syncRenderedPriceHeight={isEnrollNowButtonsAligned}
                metadata={{
                  id: 'individual',
                  title: props.programTitle,
                  features: STANDALONE_FEATURES.map((feature) => ({
                    label: feature,
                  })),
                }}
                setPriceHeight={(newHeight) =>
                  setPriceHeights((prev) => prev.map((prevHeight, idx) => (idx === 2 ? newHeight : prevHeight)))
                }
                signUpButtonSlot={
                  <IndividualSignUpButton
                    ariaLabel={`Purchase standalone access for "${props.programTitle}"`}
                    billingFrequency={billingFrequencySchema.enum.purchase}
                    colorScheme={props.buttonTheme}
                    programKey={props.programKey}
                    onClick={() =>
                      trackEvent({
                        name: 'Product Page CTA Clicked',
                        type: 'click',
                        label: 'onetime_purchase_enroll_now',
                        additionalAttributes: {
                          product_ids: [props.programKey].filter(Boolean),
                        },
                      })
                    }
                  />
                }
              />
            )}
            {props.showTeamCard && props.teamPlanMetadata && (
              <TeamPricePlan
                metadata={props.teamPlanMetadata}
                syncRenderedPriceHeight={isEnrollNowButtonsAligned}
                button={
                  <TeamSignUpButton ariaLabel='Sign up for the Team Plan' billingFrequency='monthly' colorScheme='lime'>
                    Sign Up
                  </TeamSignUpButton>
                }
                containerProps={{
                  // offset the "Best Value" label's height
                  marginTop: {
                    base: '-32px',
                    xl: '0px',
                  },
                }}
                setPriceHeight={(newHeight) =>
                  setPriceHeights((prev) => prev.map((prevHeight, idx) => (idx === 2 ? newHeight : prevHeight)))
                }
              />
            )}
          </Flex>
          {props.footerSlot}
        </Flex>
      }
      containerProps={{
        sx: {
          '--max-price-height': isEnrollNowButtonsAligned ? Math.max(...priceHeights) + 'px' : 'unset',
        },
      }}
      relatedProgramsCarouselSlot={
        <RelatedProgramsSection
          carouselTheme='light'
          programKey={props.programKey}
          relatedPrograms={props.relatedPrograms}
          title='Your subscription also includes:'
          containerProps={{
            paddingBottom: '0px',
            backgroundColor: 'unset',
            sx: {
              '--carousel-offset-start': {
                md: `48px`,
                xl: 'unset',
              },
            },
          }}
        />
      }
      sectionHeaderSlot={
        <Flex alignItems='center' flexDir='column' mb={8}>
          <Flex flexDir='column' gap={4}>
            <PromotionsBanner
              alignSelf='center'
              padding={{
                base: '8px 16px',
                xl: '8px 20px',
              }}
            />
          </Flex>
        </Flex>
      }
    />
  );
}
